import {
  CaptionProps,
  HeadlineProps,
  LinkProps,
  ParagraphProps,
  SubtitleProps,
  TextProps,
} from "./types";
import styled from "styled-components";
import { getColorByVariant, interpolateCSSProperty } from "./utils";
import {
  propsByBodyMontserrat,
  propsByBodyNunito,
  propsByHeadlineMontserrat,
  propsByHeadlineNunito,
  propsBySubtitleMontserrat,
  propsBySubtitleNunito,
} from "./consts";

const BaseTextTag = styled.p<TextProps>`
  &&& {
    padding: 0;
    margin: ${(props) => (props?.margin ? props?.margin : "0")};
    text-align: ${(props) => (props?.textAlign ? props?.textAlign : "left")};
    white-space: ${(props) => (props?.breakLines ? "pre-line" : "normal")};
    ${interpolateCSSProperty("fontSize")}
    ${interpolateCSSProperty("fontFamily")}
    ${interpolateCSSProperty("fontWeight")}
    ${interpolateCSSProperty("lineHeight")}
    ${interpolateCSSProperty("letterSpacing")}
    ${interpolateCSSProperty("color")}

    &:visited {
      color: var(--color-accent-beta-700);
    }
  }
`;

// we use this component to set defaults
export const Text = ({
  as = "p",
  fontFamily = "alpha",
  fontSize = "base",
  fontWeight = "regular",
  lineHeight = "16",
  letterSpacing = "wider",
  color = "primary-900",
  children,
  ...props
}: TextProps) => {
  return (
    <BaseTextTag
      as={as}
      fontFamily={fontFamily}
      fontSize={fontSize}
      fontWeight={fontWeight}
      lineHeight={lineHeight}
      color={color}
      letterSpacing={letterSpacing}
      {...props}
    >
      {children}
    </BaseTextTag>
  );
};

const getPropsByHeadlineType = (
  type: HeadlineProps["type"] = "h1",
  typeface: "montserrat" | "nunito" = "montserrat"
): TextProps => {
  if (typeface === "montserrat") {
    if (propsByHeadlineMontserrat[type]) {
      return {
        as: type,
        ...propsByHeadlineMontserrat[type],
      };
    }
  }

  if (typeface === "nunito") {
    if (propsByHeadlineMontserrat[type]) {
      return {
        as: type,
        ...propsByHeadlineNunito[type],
      };
    }
  }

  // returns h1 by default
  return {
    as: type,
    ...propsByHeadlineMontserrat["h1"],
  };
};

const getPropsBySubtitleType = (
  type: SubtitleProps["type"] = "1",
  typeface: "montserrat" | "nunito" = "montserrat"
): TextProps => {
  if (typeface === "montserrat") {
    if (propsBySubtitleMontserrat[type]) {
      return propsBySubtitleMontserrat[type];
    }
  }

  if (typeface === "nunito") {
    if (propsBySubtitleNunito[type]) {
      return propsBySubtitleNunito[type];
    }
  }

  // return subtitle 1 as default
  return propsBySubtitleMontserrat["1"];
};

const getPropsByBodyType = (
  type: ParagraphProps["type"] = "1",
  typeface: "montserrat" | "nunito" = "montserrat"
): TextProps => {
  if (typeface === "montserrat") {
    // added the ts ignores here to not have to think about the case
    if (propsByBodyMontserrat[type]) {
      return propsByBodyMontserrat[type];
    }
  }

  if (typeface === "nunito") {
    if (propsByBodyNunito[type]) {
      return propsByBodyNunito[type];
    }
  }

  // return paragraph 1 as default
  return propsByBodyMontserrat["1"];
};

const getCaptionProps = (typeface: "montserrat" | "nunito"): TextProps => {
  if (typeface === "montserrat") {
    return {
      as: "p",
      fontFamily: "alpha",
      fontSize: "extrasmall",
      fontWeight: "medium",
      lineHeight: "14",
      letterSpacing: "tight",
    };
  }

  if (typeface === "nunito") {
    return {
      as: "p",
      fontFamily: "alpha",
      fontSize: "extrasmall",
      fontWeight: "regular",
      lineHeight: "16",
      letterSpacing: "tight",
    };
  }

  // return paragraph 1 as default
  return {
    as: "p",
    fontFamily: "alpha",
    fontSize: "11px",
    fontWeight: "bold",
    lineHeight: "14",
    letterSpacing: "normal",
  };
};

export const Headline = ({
  children,
  typeface = "montserrat",
  type = "h1",
  color = "primary-900",
  variant,
  ...props
}: HeadlineProps) => {
  const propsByHeadlineType = getPropsByHeadlineType(type, typeface);
  const textColor = getColorByVariant(variant);

  return (
    <Text {...props} color={textColor || color} {...propsByHeadlineType}>
      {children}
    </Text>
  );
};

export const Subtitle = ({
  type = "1",
  typeface = "montserrat",
  color = "primary-900",
  variant,
  children,
  ...props
}: SubtitleProps) => {
  const propsBySubtitleType = getPropsBySubtitleType(type, typeface);
  const textColor = getColorByVariant(variant);

  return (
    <Text {...props} color={textColor || color} {...propsBySubtitleType}>
      {children}
    </Text>
  );
};

// paragraph aka body
export const Paragraph = ({
  type = "1",
  typeface = "montserrat",
  color = "primary-900",
  variant,
  children,
  ...props
}: ParagraphProps) => {
  const propsByBodyType = getPropsByBodyType(type, typeface);
  const textColor = getColorByVariant(variant);

  return (
    <Text {...props} color={textColor || color} {...propsByBodyType}>
      {children}
    </Text>
  );
};

export const Caption = ({
  typeface = "montserrat",
  color = "primary-900",
  variant,
  children,
  ...props
}: CaptionProps) => {
  const propsByBodyType = getCaptionProps(typeface);
  const textColor = getColorByVariant(variant);

  return (
    <Text {...props} color={textColor || color} {...propsByBodyType}>
      {children}
    </Text>
  );
};

export const Link = ({
  children,
  href,
  target = "_self",
  color = "secondary-800",
  ...props
}: LinkProps) => {
  return (
    <Text as="a" href={href} target={target} color={color} {...props}>
      {children}
    </Text>
  );
};
